/**
 * One study version, one number.
 *
 * The sidebar used to render a stamp frozen at study creation while the
 * workspace header rendered `Study/@VersionID` parsed from the live ODM. Since
 * `createUserStudy` was the only thing that ever wrote a version, the two were
 * structurally guaranteed to diverge as soon as a build landed on a different
 * day than creation. This makes the ODM authoritative: it is the number Anvil
 * consumes, so it is the number the UI should show everywhere.
 *
 * Pure — no localStorage, no React. The caller persists the result.
 */

import type { StudyDesignVersion } from '@/components/study-design/mock-data'
import { compareVersionIds, isOdmVersionId, studyCodeFromOdm } from '@/lib/odm/study-version-meta'

export interface ReconcilableStudy {
  slug: string
  currentVersionId: string
  versions: StudyDesignVersion[]
}

export interface StudyVersionReconciliation {
  versions: StudyDesignVersion[]
  currentVersionId: string
}

/** Same shape `createUserStudy` mints, so ids stay comparable across writers. */
export function studyVersionRowId(slug: string, versionNumber: string): string {
  return `${slug}-v${versionNumber.replace(/\./g, '-')}`
}

/**
 * Returns the registry patch needed to bring a study's version in line with
 * the live ODM, or null when nothing should change.
 *
 * Null on no-op is load-bearing: `saveRaw` dispatches USER_STUDIES_UPDATED,
 * which reloads the nav provider, so writing on every render would loop.
 */
export function reconcileStudyOdmVersion(
  study: ReconcilableStudy,
  odmVersionId: string | null | undefined,
  now: string = new Date().toISOString(),
): StudyVersionReconciliation | null {
  const target = odmVersionId?.trim()

  // A lifecycle version ("1.1"), a status label or a half-written stamp must
  // never overwrite the study version.
  if (!target || !isOdmVersionId(target)) return null

  const current = study.versions.find(v => v.id === study.currentVersionId)

  if (current) {
    if (current.versionNumber === target) return null
    // Only ever advance. The XML tab can display a previously saved snapshot,
    // and adopting that would silently downgrade the sidebar.
    if (
      isOdmVersionId(current.versionNumber) &&
      compareVersionIds(target, current.versionNumber) <= 0
    ) {
      return null
    }
  }

  const existing = study.versions.find(v => v.versionNumber === target)
  if (existing) {
    return { versions: study.versions, currentVersionId: existing.id }
  }

  const row: StudyDesignVersion = {
    id: studyVersionRowId(study.slug, target),
    versionNumber: target,
    status: 'draft',
    createdAt: now,
    createdBy: 'You',
    changeSummary: `Build ${target}`,
  }

  return { versions: [...study.versions, row], currentVersionId: row.id }
}

/**
 * History rows to list beneath the live-ODM entry in the version picker.
 *
 * Two exclusions, and both are needed. The selected row is dropped because
 * the live-ODM entry is rendered with that same `value`. The row whose
 * versionNumber equals the live ODM is dropped because after reconciliation
 * that row exists in history while the URL's versionId still points at the
 * older one — without it the dropdown listed the same version twice, once as
 * "(live ODM)" and once as "draft".
 */
export function studyVersionPickerRows<T extends { id: string; versionNumber: string }>(
  versions: readonly T[],
  selectedVersionId: string,
  odmVersionId: string | null,
): T[] {
  if (!odmVersionId) return [...versions]
  return versions.filter(
    v => v.id !== selectedVersionId && v.versionNumber !== odmVersionId,
  )
}

/** Same normalisation `resolveArtifactStudyCode` uses, so both agree. */
function normalizeStudyCode(value: string): string {
  return value.trim().toUpperCase().replace(/[^\w-]+/g, '-').replace(/-+/g, '-')
}

/**
 * Does this ODM document actually describe the open study?
 *
 * The live xml content tab is not keyed to the workspace — it can still hold
 * STUDY-ABC demo scaffold, or another study's merge, while a real study is
 * open. `resolveArtifactStudyCode` exists for exactly this on the filename
 * path; adopting a foreign VersionID would misattribute a build to the wrong
 * study, which is worse than showing a stale one.
 *
 * The build path makes this a real invariant rather than a hope: the shell
 * worker's tool schema defines `study_name` as the "short uppercase study
 * identifier ... becomes the Study OID", and a rig run produced
 * StudyName="QUICKWAVE" for study QUICKWAVE.
 */
export function odmMatchesStudy(
  meta: { studyName: string; studyOid: string } | null | undefined,
  studyCode: string | null | undefined,
): boolean {
  if (!meta) return false
  const expected = studyCode?.trim()
  if (!expected) return false
  return normalizeStudyCode(studyCodeFromOdm(meta)) === normalizeStudyCode(expected)
}
